import { useCallback, useRef, useState } from 'react';
import { ChevronDown, GripVertical, Plus, X } from 'lucide-react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@/lib/utils';

// =====================================================================
// Repeater — sortable, expandable, single-expand list.
//
// Heart of every complex block (services items, pricing tiers, gallery
// images, team members, faq questions, credentials chips). Items have
// no schema-level id; the Repeater synthesizes stable ids internally
// for dnd-kit and React keys, then strips them on the way out to
// onChange.
//
// id sync policy: when the items prop arrives from the parent with a
// different LENGTH than we have ids for, we rebuild. When the length
// is the same, we keep ids by index (works for inline edits, undo of
// inline edits, and dnd-kit reorders which we ourselves drive). If the
// parent reorders items externally (rare — only via Cmd+Z undo of a
// previous Repeater-driven reorder), the visual id-to-item association
// may shuffle for one render; behavior stays correct.
// =====================================================================

function makeId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
}

interface RepeaterProps<T> {
  items: T[];
  onChange: (next: T[]) => void;
  renderTitle: (item: T, index: number) => React.ReactNode;
  renderForm: (item: T, index: number, update: (patch: Partial<T>) => void) => React.ReactNode;
  addLabel: string;
  makeNew: () => T;
  /** Hide delete on the last row (preserves Zod .min(1) schemas). */
  preventEmpty?: boolean;
}

export function Repeater<T>({
  items,
  onChange,
  renderTitle,
  renderForm,
  addLabel,
  makeNew,
  preventEmpty = true,
}: RepeaterProps<T>) {
  const idsRef = useRef<string[]>([]);
  // Sync ids array length with items length on every render.
  while (idsRef.current.length < items.length) idsRef.current.push(makeId());
  if (idsRef.current.length > items.length) {
    idsRef.current = idsRef.current.slice(0, items.length);
  }

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const updateItem = useCallback(
    (index: number, patch: Partial<T>) => {
      onChange(items.map((it, i) => (i === index ? { ...it, ...patch } : it)));
    },
    [items, onChange],
  );

  const deleteItem = useCallback(
    (index: number) => {
      if (preventEmpty && items.length <= 1) return;
      const removedId = idsRef.current[index];
      if (removedId && expandedId === removedId) setExpandedId(null);
      idsRef.current = idsRef.current.filter((_, i) => i !== index);
      onChange(items.filter((_, i) => i !== index));
    },
    [items, onChange, expandedId, preventEmpty],
  );

  const addItem = useCallback(() => {
    const next = makeNew();
    const newId = makeId();
    idsRef.current = [...idsRef.current, newId];
    onChange([...items, next]);
    setExpandedId(newId);
  }, [items, onChange, makeNew]);

  const onDragEnd = useCallback(
    (e: DragEndEvent) => {
      const { active, over } = e;
      if (!over || active.id === over.id) return;
      const oldIndex = idsRef.current.indexOf(String(active.id));
      const newIndex = idsRef.current.indexOf(String(over.id));
      if (oldIndex < 0 || newIndex < 0) return;
      idsRef.current = arrayMove(idsRef.current, oldIndex, newIndex);
      onChange(arrayMove(items, oldIndex, newIndex));
    },
    [items, onChange],
  );

  const ids = idsRef.current;

  return (
    <div className="flex flex-col gap-2">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {items.map((item, index) => {
            const id = ids[index];
            if (!id) return null;
            return (
              <RepeaterRow
                key={id}
                rowId={id}
                expanded={expandedId === id}
                canDelete={!(preventEmpty && items.length <= 1)}
                onExpand={() => setExpandedId(id)}
                onCollapse={() => setExpandedId(null)}
                onDelete={() => deleteItem(index)}
                title={renderTitle(item, index)}
                form={renderForm(item, index, (patch) => updateItem(index, patch))}
              />
            );
          })}
        </SortableContext>
      </DndContext>
      <button
        type="button"
        onClick={addItem}
        className={cn(
          'flex items-center justify-center gap-2 w-full h-10',
          'mt-1 text-sm font-medium text-brand-700',
          'bg-brand-50 hover:bg-brand-100',
          'border border-dashed border-brand-200',
          'rounded-[var(--radius-sm)]',
          'transition-colors duration-[var(--duration-fast)]',
          'focus-visible:outline-2 focus-visible:outline-brand-500',
        )}
      >
        <Plus className="h-4 w-4" aria-hidden />
        {addLabel}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------
// Individual row — collapsed (40px) or expanded (title strip + form).
// ---------------------------------------------------------------------

interface RepeaterRowProps {
  rowId: string;
  expanded: boolean;
  canDelete: boolean;
  onExpand: () => void;
  onCollapse: () => void;
  onDelete: () => void;
  title: React.ReactNode;
  form: React.ReactNode;
}

function RepeaterRow({
  rowId,
  expanded,
  canDelete,
  onExpand,
  onCollapse,
  onDelete,
  title,
  form,
}: RepeaterRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: rowId,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  if (expanded) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className={cn(
          'rounded-[var(--radius)]',
          'bg-card border border-border',
          'overflow-hidden',
        )}
      >
        <div
          className={cn(
            'flex items-center gap-2',
            'h-[var(--repeater-row-collapsed)] pl-1 pr-2',
            'border-b border-border-subtle',
            'bg-surface-sunken',
          )}
        >
          <button
            type="button"
            {...attributes}
            {...listeners}
            aria-label="Drag to reorder"
            className={cn(
              'shrink-0 p-1.5 rounded',
              'text-text-tertiary hover:text-text-secondary',
              'cursor-grab active:cursor-grabbing',
              'focus-visible:outline-2 focus-visible:outline-brand-500',
            )}
          >
            <GripVertical className="h-3.5 w-3.5" aria-hidden />
          </button>
          <button
            type="button"
            onClick={onCollapse}
            className={cn(
              'flex-1 min-w-0 text-left text-sm text-foreground truncate',
              'inline-flex items-center gap-1',
              'focus-visible:outline-2 focus-visible:outline-brand-500',
              'rounded',
            )}
          >
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-text-tertiary" aria-hidden />
            <span className="truncate">{title}</span>
          </button>
          {canDelete ? (
            <button
              type="button"
              onClick={onDelete}
              aria-label="Delete item"
              className={cn(
                'shrink-0 p-1.5 rounded text-text-tertiary',
                'hover:text-danger-fg hover:bg-danger-bg',
                'focus-visible:outline-2 focus-visible:outline-brand-500',
                'transition-colors duration-[var(--duration-fast)]',
              )}
            >
              <X className="h-3.5 w-3.5" aria-hidden />
            </button>
          ) : null}
        </div>
        <div className="p-4 flex flex-col gap-3">{form}</div>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'group/row',
        'flex items-center gap-2',
        'h-[var(--repeater-row-collapsed)] pl-1 pr-2',
        'rounded-[var(--radius-sm)]',
        'bg-surface-sunken border border-border-subtle',
        'hover:border-border',
        'transition-colors duration-[var(--duration-fast)]',
      )}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
        className={cn(
          'shrink-0 p-1.5 rounded',
          'text-text-tertiary hover:text-text-secondary',
          'cursor-grab active:cursor-grabbing',
          'focus-visible:outline-2 focus-visible:outline-brand-500',
        )}
      >
        <GripVertical className="h-3.5 w-3.5" aria-hidden />
      </button>
      <button
        type="button"
        onClick={onExpand}
        className={cn(
          'flex-1 min-w-0 text-left text-sm text-foreground truncate',
          'py-1 rounded',
          'focus-visible:outline-2 focus-visible:outline-brand-500',
        )}
      >
        <span className="truncate">{title}</span>
      </button>
      {canDelete ? (
        <button
          type="button"
          onClick={onDelete}
          aria-label="Delete item"
          className={cn(
            'shrink-0 p-1.5 rounded text-text-tertiary',
            'opacity-0 group-hover/row:opacity-100',
            'focus-visible:opacity-100',
            'hover:text-danger-fg hover:bg-danger-bg',
            'focus-visible:outline-2 focus-visible:outline-brand-500',
            'transition-opacity duration-[var(--duration-fast)]',
          )}
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      ) : null}
    </div>
  );
}

// Helper for the common "Untitled" / fallback title pattern.
export function RepeaterTitle({ children }: { children: React.ReactNode }) {
  if (!children || (typeof children === 'string' && !children.trim())) {
    return <em className="text-text-tertiary not-italic">Untitled</em>;
  }
  return <>{children}</>;
}
